import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { computed, useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import { useEffect } from "react"

import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle.jsx"
import { NotificationTypes, addNotification } from "../../stores/notification-store"
import { SortDirectionType } from '../../utils/constants'
import { computeSortedData, generateJsonFromSchema } from '../../utils/utils'
import { CreateProfile, DeleteProfile } from "../../utils/api.js"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"

import useProfiles from "../../hooks/useProfiles.js"
import useProfileSchema from "../../hooks/useProfileSchema.js"
import useSchemaManifest from "../../hooks/useSchemaManifest.js"

export default function ProfileList() {

    const navigate = useNavigate()
    const { beId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData  } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value])
    
    const canEditSignal = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    const { manifest: manifestSignal } = useSchemaManifest()
    const profileSchemaVersionsSignal = useComputed(() => {
        if (manifestSignal.value?.profiles) {
            const versions = manifestSignal.value.profiles.map(p => p.utgVersion)
            return versions.sort()
        }
        return []
    })
    const defaultProfileSchemaVersionSignal = useComputed(() => {
        if (manifestSignal.value?.profiles) {
            const version = manifestSignal.value.profiles.find(p => p.default)
            return version
        }
    })
    useSignalEffect(() => {
        newProfileUtgVersionSignal.value = defaultProfileSchemaVersionSignal.value?.utgVersion
    })

    const { profiles: profilesSignal, loading: loadingSignal, error, modified: modifiedSignal, fetchData: fetchProfilesData } = useProfiles(beId, config, accessToken)
    const profileNamesSignal = useComputed(() => {
        if (profilesSignal.value) {
            return profilesSignal.value.map((p) => p.name)
        }
        return []
    })

    // setting utgVersion to null will prevent loading schema until we are ready later
    const { profileSchema, isLoaded, fetchData } = useProfileSchema(beId, null, manifestSignal.value, config, accessToken)

    // Action toolbar
    const handleToolSelect = item => {
        if (item == 'new') {
            showModalNewSignal.value = true
        }
        if (item == 'about') {
            console.log('not implemented yet')
        }
    }

    // Clone profile modal
    const showModalCloneSignal = useSignal(false)
    const cloneProfileItemSignal = useSignal({})
    const disableCloneSubmitSignal = computed(() => {
        return (newProfileNameSignal.value.length == 0 || nameAlreadyUsedSignal.value)
    })
    const handleCloneProfileShowModal = item => {
        if (item) {
            cloneProfileItemSignal.value = item
            showModalCloneSignal.value = true
        }
    }
    const handleSubmitModalClone = async () => {
        showModalCloneSignal.value = false
        showSpinnerSignal.value = true
        try {
            const profileConfig = {
                "name": newProfileNameSignal.value,
                "businessEntityId": beId,
                "type": 1,
                "config": cloneProfileItemSignal.value.config
            }
            const profileResult = await CreateProfile(beId, profileConfig, config, accessToken)
            modifiedSignal.value = true;
            addNotification('Profile successfully cloned', 'success', true)
            navigate(`./${profileResult.componentProfile.id}`)
        } catch (error) {
            addNotification(`Unable to clone profile. ${error}`)
        }
        finally {
            showSpinnerSignal.value = false
        }
    }

    // Delete profile modal
    const showModalDeleteSignal = useSignal(false)
    const deleteProfileItemSignal = useSignal({})
    const deleteProfileConfirmNameSignal = useSignal('')
    const disableDeleteSubmitSignal = computed(() => {
        if (deleteProfileItemSignal.value.name) {
            return deleteProfileItemSignal.value.name != deleteProfileConfirmNameSignal.value
        }
    })
    const handleDeleteProfileShowModal = item => {
        if (item) {
            deleteProfileItemSignal.value = item
            showModalDeleteSignal.value = true
        }
    }
    const handleSubmitModalDelete = async () => {
        showModalDeleteSignal.value = false
        try {
            /*const profileResult =*/ await DeleteProfile(beId, deleteProfileItemSignal.value.id, deleteProfileItemSignal.value.name, config, accessToken)
            modifiedSignal.value = true; // flag useProfiles to reload data when called again
            addNotification('Profile successfully deleted', 'success', true)  
            await fetchProfilesData(beId, config, accessToken)
        } catch (error) {
            addNotification(`Unable to delete profile. ${error}`)
        }
    }

    // New profile modal
    const showModalNewSignal = useSignal(false)
    const newProfileNameSignal = useSignal('') // server enforces uniqueness within be
    const nameAlreadyUsedSignal = useComputed(() => {        
        return profileNamesSignal.value.length && profileNamesSignal.value.includes(newProfileNameSignal.value)
    })
    const newProfileUtgVersionSignal = useSignal('')
    const disableSubmitSignal = useComputed(() => {
        return (newProfileNameSignal.value?.length == 0 || newProfileUtgVersionSignal.value?.length == 0 || nameAlreadyUsedSignal.value)
    })
    const showSpinnerSignal = useSignal(false)
    const handleCloseModalNew = () => showModalNewSignal.value = false
    const handleSubmitModalNew = async () => {
        showModalNewSignal.value = false
        showSpinnerSignal.value = true

        try {
            // load the specified schema, generate default profile from it and navigate to edit profile pages
            const schema = await fetchData(beId, newProfileUtgVersionSignal.value, manifestSignal.value, config, accessToken)
            if (typeof schema == 'undefined') {
                throw new Error("fetchData returned nothing")
            }
            const jsonProfile = generateJsonFromSchema(schema)

            // add the schema verson to the config for easy tracking
            jsonProfile.config.utgGlobal.ccschema = newProfileUtgVersionSignal
            const profileConfig = {
                "name": newProfileNameSignal.value,
                "businessEntityId": beId,
                "type": 1,
                "config": jsonProfile.config
            }
            const profileResult = await CreateProfile(beId, profileConfig, config, accessToken)
            modifiedSignal.value = true; // flag useProfiles to reload data when called again
            addNotification('Profile successfully created', 'success', true)
            navigate(`./${profileResult.componentProfile.id}`)
        } catch (error) {
            addNotification(`Unable to create profile. ${error}`)
        }
        finally {
            showSpinnerSignal.value = false
        }
    }

    // Table
    const currentPageSignal = useSignal(1)
    const rowsPerPageSignal = useSignal(parseInt(window.localStorage.getItem('pagesize')) || 250)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')

    const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    useSignalEffect(() => {
        window.localStorage.setItem('pagesize', rowsPerPageSignal.value)
    })

    const filteredDataSignal = useComputed(() => {
        if (!filterStringSignal.value) {
            return profilesSignal.value ? [...profilesSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return profilesSignal.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                name: item.name,
                businessEntityName: item.businessEntitySummary.name,
                version: item.version,
                schema: item.config?.utgGlobal?.ccschema
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof(value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedDataSignal = useComputed(computeSortedData(filteredDataSignal, sortColumnSignal, sortDirectionSignal))

    const totalPagesSignal = useComputed(() => {
        return Math.ceil(filteredDataSignal.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayDataSignal = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedDataSignal.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumberSignal = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumberSignal = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredDataSignal.value.length ? endIndex : filteredDataSignal.value.length
    })

    const accordionData = [
        { name: 'Profiles', data: profilesSignal.value },
        { name: 'Display Data', data: displayDataSignal.value },
        { name: 'Sorted Data', data: sortedDataSignal.value },
        { name: 'Filtered Data', data: filteredDataSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: manifestSignal.value },
        { name: 'Profile Schema Versions', data: profileSchemaVersionsSignal.value }
    ]

    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        rowsPerPageSignal.value = parseInt(v, 10)
    }
    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }
    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText)
            {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.name} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }


    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    function TableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.id}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width={16} height={16}></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}><Icon icon="clipboard" width={16} height={16}></Icon> Copy Row as JSON</Dropdown.Item>
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleDeleteProfileShowModal(item)}><Icon icon="trash" width={16} height={16}></Icon> Delete profile</Dropdown.Item>}
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleCloneProfileShowModal(item)}><Icon icon="copy" width={16} height={16}></Icon> Clone profile</Dropdown.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td><Link to={`../../be/${item.businessEntityId}/profiles/${item.id}`}>{item.name}</Link></td>
                <td>{(item.businessEntitySummary && item.businessEntitySummary.name) ? item.businessEntitySummary.name : ''}</td>
                <td>{item.version}</td>
                <td><TimestampWithUTCTooltip date={item.createdAt}></TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip date={item.lastUpdatedAt}></TimestampWithUTCTooltip></td>
                <td>{(item.config.utgGlobal && item.config.utgGlobal.ccschema) ? item.config.utgGlobal.ccschema : ''}</td>
                <td>{item.config?.deviceConfig?.device?.length}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPagesSignal.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item> 
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-down" width={16} height={16}></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width={16} height={16}></Icon>
            </th>
        )
    }

    if (showSpinnerSignal.value || loadingSignal.value || !profilesSignal.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>

            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Profile List' canEditSignal={canEditSignal} />
            </Stack>
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <DropdownButton variant="outline-secondary" className="me-auto" title={`${rowsPerPageSignal} records per page`} id="pageSize-dropdown" onSelect={handlePageSizeSelect}>
                    <Dropdown.Item eventKey="10">10</Dropdown.Item>
                    <Dropdown.Item eventKey="25">25</Dropdown.Item>
                    <Dropdown.Item eventKey="50">50</Dropdown.Item>
                    <Dropdown.Item eventKey="250">250</Dropdown.Item>
                    <Dropdown.Item eventKey="500">500</Dropdown.Item>
                    <Dropdown.Item eventKey="1000">1000</Dropdown.Item>
                </DropdownButton>
                <Nav.Item className="mt-1 me-2">
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                {canEditSignal.value && <Nav.Item className="mt-1">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showModalNewSignal.value = true}><Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New</Button>
                </Nav.Item>}
            </Nav>
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <TableColumnHeader title={'Action'} sortable={false}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Name'} sortable={true} datafield={'name'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'businessEntitySummary.name'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Version'} sortable={true} datafield={'version'}></TableColumnHeader>
                        <TableColumnHeader title={'Creation Date'} sortable={true} datafield={'createdAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Updated'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Schema'} sortable={true} datafield={'config.utgGlobal.ccschema'}></TableColumnHeader>
                        <TableColumnHeader title={'Devices'} sortable={false}></TableColumnHeader>
                    </tr>
                </thead>
                <TableRows items={displayDataSignal.value} />
                <tfoot><tr><td className="fs-6" colSpan={19}>Displaying {startDisplayRowNumberSignal.value}-{endDisplayRowNumberSignal.value} of {filteredDataSignal.value.length}</td></tr></tfoot>
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debug} />

            <Modal show={showModalNewSignal.value} onHide={handleCloseModalNew}>
                <Modal.Header closeButton>
                    <Modal.Title>Add a New Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
                            <Form.Label>Profile Name</Form.Label>
                            <Form.Control
                                type="text"
                                required
                                onInput={(e) => newProfileNameSignal.value = e.target.value}
                                autoFocus
                                value={newProfileNameSignal.value}
                            />
                            <p className="mt-1" hidden={!nameAlreadyUsedSignal.value}>Profile name must be unique</p>
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="exampleForm.ControlTextarea1"
                        >
                            <Form.Label>UTG Version</Form.Label>
                            <Form.Select aria-label="Utg Version Profile Schema"
                                required
                                value={newProfileUtgVersionSignal.value}
                                onChange={(e) => newProfileUtgVersionSignal.value = e.target.value}>
                                {profileSchemaVersionsSignal.value.map(v => <option key={v} value={v}>{v}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleCloseModalNew}>
                        Cancel
                    </Button>
                    <Button disabled={disableSubmitSignal.value} variant="primary" onClick={handleSubmitModalNew}>
                        Add New
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showModalDeleteSignal.value} onHide={() => showModalDeleteSignal.value = false}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Profile {deleteProfileItemSignal.value.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Deleting a profile is a permanent action that cannot be reversed. Once a profile is deleted, you will no longer be able to view the associated services or installations. Any installation packages using this profile will no longer be accessible. Additionally, you will no longer be able to track version differences between the profile and services that have previously leveraged this profile.</p>
                    <p>If you are sure you want to delete this profile, please enter the name of the profile below and select &quot;Delete&quot; to complete the process.</p>
                    <Form>
                        <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
                            <Form.Label>Profile Name: {deleteProfileItemSignal.value.name}</Form.Label>
                            <Form.Control
                                type="text"
                                required
                                onInput={(e) => deleteProfileConfirmNameSignal.value = e.target.value}
                                autoFocus
                                defaultValue={newProfileNameSignal.value}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => showModalDeleteSignal.value = false}>
                        Cancel
                    </Button>
                    <Button disabled={disableDeleteSubmitSignal.value} variant="danger" onClick={handleSubmitModalDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showModalCloneSignal.value} onHide={() => showModalCloneSignal.value = false}>
                <Modal.Header closeButton>
                    <Modal.Title>Clone Profile {cloneProfileItemSignal.value.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Enter the name of the new profile below. This profile will carry over all the configuration settings from {cloneProfileItemSignal.value.name}</p>
                    <Form>
                        <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
                            <Form.Label>Profile Name</Form.Label>
                            <Form.Control
                                type="text"
                                required
                                onInput={(e) => newProfileNameSignal.value = e.target.value}
                                autoFocus
                                value={newProfileNameSignal.value}
                            />
                            <p className="mt-1" hidden={!nameAlreadyUsedSignal.value}>Profile name must be unique</p>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => showModalCloneSignal.value = false}>
                        Cancel
                    </Button>
                    <Button disabled={disableCloneSubmitSignal.value} variant="primary" onClick={handleSubmitModalClone}>
                        Clone
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    )
}